import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Minus, Search } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { OrderWindowBanner } from "../components/OrderWindowBanner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EXTRA_PROTEINS = [
  { name: "Chicken", price: 4 },
  { name: "Shrimp", price: 5 },
  { name: "Salmon", price: 7 },
  { name: "Steak", price: 6 }
];

export const Menu = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [selectedExtras, setSelectedExtras] = useState({}); // Track extra proteins per item

  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/menu-items?menu_type=meal_prep`);
        setMenuItems(data.filter(item => item.available));
      } catch (error) {
        toast.error("Failed to load menu items");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    const loadCart = () => {
      const savedCart = localStorage.getItem("cart");
      if (savedCart) {
        setCart(JSON.parse(savedCart));
      }
    };

    fetchMenuItems();
    loadCart();
  }, []);

  const categories = ["All", ...new Set(menuItems.map(item => item.category))];

  const filteredItems = menuItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "All" || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const addToCart = (item) => {
    const extras = selectedExtras[item.id] || [];
    const extrasPrice = extras.reduce((sum, extra) => sum + extra.price, 0);
    const totalPrice = item.price + extrasPrice;
    
    const existingItem = cart.find(cartItem => 
      cartItem.menu_item_id === item.id && 
      JSON.stringify(cartItem.extras) === JSON.stringify(extras)
    );
    
    let newCart;
    
    if (existingItem) {
      newCart = cart.map(cartItem =>
        cartItem.menu_item_id === item.id && JSON.stringify(cartItem.extras) === JSON.stringify(extras)
          ? { ...cartItem, quantity: cartItem.quantity + 1 }
          : cartItem
      );
    } else {
      const itemName = extras.length > 0 
        ? `${item.name} (+ ${extras.map(e => e.name).join(', ')})`
        : item.name;
      
      newCart = [...cart, { 
        menu_item_id: item.id,
        name: itemName,
        price: totalPrice,
        quantity: 1,
        image: item.image,
        extras: extras
      }];
    }
    
    setCart(newCart);
    localStorage.setItem("cart", JSON.stringify(newCart));
    toast.success(`${item.name} added to cart!`);
    setSelectedExtras(prev => ({ ...prev, [item.id]: [] })); // Reset extras for this item
  };

  const toggleExtraProtein = (itemId, protein) => {
    setSelectedExtras(prev => {
      const current = prev[itemId] || [];
      const exists = current.find(p => p.name === protein.name);
      
      if (exists) {
        return {
          ...prev,
          [itemId]: current.filter(p => p.name !== protein.name)
        };
      } else {
        return {
          ...prev,
          [itemId]: [...current, protein]
        };
      }
    });
  };

  const getItemQuantity = (itemId) => {
    const item = cart.find(cartItem => cartItem.menu_item_id === itemId);
    return item ? item.quantity : 0;
  };

  const updateQuantity = (itemId, change) => {
    const newCart = cart.map(cartItem => {
      if (cartItem.menu_item_id === itemId) {
        const newQuantity = cartItem.quantity + change;
        return newQuantity > 0 ? { ...cartItem, quantity: newQuantity } : null;
      }
      return cartItem;
    }).filter(Boolean);
    
    setCart(newCart);
    localStorage.setItem("cart", JSON.stringify(newCart));
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-28 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading menu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-stone-50 to-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <OrderWindowBanner />
        
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border-emerald-200 mb-4">
            Fresh • Healthy • Delicious
          </Badge>
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-4 leading-tight">
            Chef Prepared
            <span className="block text-emerald-600">Delivered Fresh</span>
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Experience the perfect blend of flavor and nutrition with Raffy's Cuisine.
            Chef Eric crafts every meal with passion, bringing restaurant-quality food to your home.
          </p>
        </div>

        {/* Search and Filter */}
        <div className="mb-8 space-y-6">
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              type="text"
              placeholder="Search meals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 py-6 text-lg border-2 focus:border-emerald-500"
            />
          </div>

          <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="w-full">
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-4 h-auto p-1 bg-stone-100">
              {categories.map((category) => (
                <TabsTrigger
                  key={category}
                  value={category}
                  className="text-base py-3 data-[state=active]:bg-emerald-600 data-[state=active]:text-white"
                >
                  {category}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Menu Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredItems.map((item, index) => {
            const quantity = getItemQuantity(item.id);
            
            return (
              <Card
                key={item.id}
                className="group overflow-hidden border-2 hover:border-emerald-300 transition-all duration-300 hover:shadow-2xl animate-in fade-in zoom-in"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="relative overflow-hidden h-64">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  {item.popular && (
                    <Badge className="absolute top-4 right-4 bg-amber-500 text-white border-amber-600 shadow-lg">
                      Popular
                    </Badge>
                  )}
                  <div className="absolute bottom-4 right-4 bg-emerald-600 text-white px-4 py-2 rounded-lg font-bold text-xl shadow-lg">
                    ${item.price.toFixed(2)}
                  </div>
                </div>

                <CardContent className="p-6 space-y-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{item.name}</h3>
                    <Badge variant="outline" className="text-emerald-700 border-emerald-300 mb-3">
                      {item.category} • {item.protein}
                    </Badge>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </div>

                  {/* Extra Protein Options */}
                  <div className="border-t pt-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Add Extra Protein:</p>
                    <div className="flex flex-wrap gap-2">
                      {EXTRA_PROTEINS.map((protein) => {
                        const isSelected = selectedExtras[item.id]?.some(p => p.name === protein.name);
                        return (
                          <button
                            key={protein.name}
                            type="button"
                            onClick={() => toggleExtraProtein(item.id, protein)}
                            className={`px-3 py-1 text-xs rounded-full border-2 transition-colors ${
                              isSelected 
                                ? 'bg-emerald-600 text-white border-emerald-600' 
                                : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-300'
                            }`}
                          >
                            {protein.name} +${protein.price}
                          </button>
                        );
                      })}
                    </div>
                    {selectedExtras[item.id]?.length > 0 && (
                      <p className="text-sm text-emerald-600 mt-2 font-semibold">
                        +${selectedExtras[item.id].reduce((sum, p) => sum + p.price, 0)} extra
                      </p>
                    )}
                  </div>

                  {quantity === 0 ? (
                    <Button
                      onClick={() => addToCart(item)}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg font-semibold transition-all duration-200 hover:scale-105"
                    >
                      <Plus className="mr-2 h-5 w-5" />
                      Add to Cart
                    </Button>
                  ) : (
                    <div className="flex items-center justify-between bg-emerald-50 rounded-lg p-2 border-2 border-emerald-200">
                      <Button
                        onClick={() => updateQuantity(item.id, -1)}
                        variant="ghost"
                        size="icon"
                        className="hover:bg-emerald-100 text-emerald-700"
                      >
                        <Minus className="h-5 w-5" />
                      </Button>
                      <span className="text-xl font-bold text-emerald-700 px-4">
                        {quantity}
                      </span>
                      <Button
                        onClick={() => updateQuantity(item.id, 1)}
                        variant="ghost"
                        size="icon"
                        className="hover:bg-emerald-100 text-emerald-700"
                      >
                        <Plus className="h-5 w-5" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredItems.length === 0 && (
          <div className="text-center py-20">
            <p className="text-xl text-gray-600">No meals found matching your search.</p>
          </div>
        )}
      </div>
    </div>
  );
};
